// BatchPaymentReportPage.tsx
import React, { memo, useMemo, useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getAuthCookie } from "../../utils/authCookies";
import PaymentSection from "./PaymentSection";
import SendOrderEmailRecipientsModal, {
  type SendOrderEmailRecipient,
} from "components/SendOrderEmailRecipientsModal";
import OrderLog, { buildBatchPaymentLogEventsFromReport } from "components/OrderLog";
import { showError, showSuccess } from "toast/toast";
// import { buildBatchPaymentReportInvoicePdfAttachment } from "utils/installationOrderPdfAttachment";
import {
  buildBatchInvoiceEmailParams,
  // buildBatchInvoicePublicViewPayUrl,
  sendBatchInvoiceTriggerEmails,
} from "utils/invoiceEmailHelpers";
import { SalesOrderStatusBadge } from "components/SalesOrderstatus";

/* =========================
   Types
========================= */
type Money = number;
type Company = { name: string; address1: string; address2?: string; phone?: string; email?: string };
type Party = {
  name: string;
  addressLabel?: string;
  address1?: string;
  address2?: string;
  phone?: string;
};
type LineItem = {
  description: string;
  qty: number;
  unitPrice: Money;
  total: Money;
  isCost?: boolean;
};
type OrderRow = {
  orderId: string;
  type: "Sales" | "Installation" | "Removal" | "Unknown";
  office: string;
  agent: string;
  orderDate: string;
  dueDate: string;
  amount?: Money;
  paid?: Money;
  balance?: Money;
  hasDetail: boolean;
  address?: { label: string; text: string };
  items?: LineItem[];
  costLines?: LineItem[];
};
type Summary = { 
  itemsTotal?: Money; 
  discount?: Money;
  installationCost?: Money;
  removalCost?: Money;
  specialFee?: Money; 
  shipping?: Money; 
  tax?: Money; 
  grandTotal?: Money; 
  paid?: Money;
  outstandingBalance?: Money;
};

/* =========================
   Utils
========================= */
const currency = (n: number) =>
  (isNaN(n) ? 0 : n).toLocaleString(undefined, { style: "currency", currency: "USD" });
const sum = (a: number[]) => a.reduce((x, y) => x + y, 0);

const hasEntries = (arr: unknown): arr is any[] => Array.isArray(arr) && arr.length > 0;

const firstText = (...values: unknown[]) =>
  values.map((v) => String(v ?? "").trim()).find(Boolean) ?? "";

const formatReportDate = (...values: unknown[]) => {
  for (const value of values) {
    const raw = String(value ?? "").trim();
    if (!raw || raw.startsWith("0001-01-01")) continue;
    const date = new Date(raw);
    if (!isNaN(date.getTime())) return date.toLocaleDateString();
  }
  return "—";
};

type ReportVersion = {
  source: "snapshot" | "live";
  rows: any[];
  details: any[];
  totals: any;
};

/**
 * One consistent version per report, never mixed:
 * Draft → rows + details + totals.
 * Any other invoice status → snapshot_rows + snapshot_details + snapshot_totals,
 * unless the snapshot pair is incomplete and the live pair is complete (legacy reports).
 * Payment status never affects this choice.
 */
const pickReportVersion = (report: any): ReportVersion => {
  const snapshot: ReportVersion = {
    source: "snapshot",
    rows: hasEntries(report?.snapshot_rows) ? report.snapshot_rows : [],
    details: hasEntries(report?.snapshot_details) ? report.snapshot_details : [],
    totals: report?.snapshot_totals ?? {},
  };
  const live: ReportVersion = {
    source: "live",
    rows: hasEntries(report?.rows) ? report.rows : [],
    details: hasEntries(report?.details) ? report.details : [],
    totals: report?.totals ?? {},
  };
  const isComplete = (v: ReportVersion) => v.rows.length > 0 && v.details.length > 0;
  const isDraft = String(report?.invoice_status ?? "").trim().toLowerCase() === "draft";

  if (isDraft) return live;
  if (isComplete(snapshot) || !isComplete(live)) return snapshot;
  return live;
};

const resolveOrderType = (raw?: string): OrderRow["type"] => {
  switch (String(raw ?? "").trim().toLowerCase()) {
    case "sales":
      return "Sales";
    case "installation":
    case "service_install":
      return "Installation";
    case "removal":
      return "Removal";
    default:
      return "Unknown";
  }
};

const COST_LINE_LABELS = ["installation cost", "removal cost"];

const isCostLabel = (description: string) =>
  COST_LINE_LABELS.includes(description.trim().toLowerCase());

const toLineItem = (item: any): LineItem => {
  const qty = Number(item?.qty ?? item?.quantity ?? 0);
  const unitPrice = Number(item?.unit_price ?? item?.unitPrice ?? 0);
  const description = firstText(item?.name, item?.description) || "—";
  return {
    description,
    qty,
    unitPrice,
    total: Number(item?.total ?? qty * unitPrice),
    isCost: isCostLabel(description),
  };
};

// Skips a cost the backend already sent as a line in `items`.
const toCostLines = (
  detail: any,
  type: OrderRow["type"],
  items: LineItem[]
): LineItem[] => {
  const lines: LineItem[] = [];
  const addCost = (label: string, value: unknown, applies: boolean) => {
    if (value === null || value === undefined) return;
    if (items.some((it) => it.description.trim().toLowerCase() === label.toLowerCase())) return;
    const amount = Number(value);
    if (!applies && amount === 0) return;
    lines.push({ description: label, qty: 1, unitPrice: amount, total: amount, isCost: true });
  };
  addCost("Installation Cost", detail?.installation_cost, type === "Installation");
  addCost("Removal Cost", detail?.removal_cost, type === "Removal");
  return lines;
};

// Accepts a plain string, an address object, or an Addresses record with `address_details`.
const formatAddress = (value: any): string => {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  const a = value.address_details ?? value;
  return [a.addressLine1, a.addressLine2, a.city, a.state, a.zip, a.country]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(", ");
};

const toOrderAddress = (
  detail: any,
  row: any,
  type: OrderRow["type"]
): OrderRow["address"] => {
  if (type !== "Installation" && type !== "Removal") return undefined;
  const field = type === "Installation" ? "installation_address" : "removal_address";
  const text = formatAddress(detail?.[field]) || formatAddress(row?.[field]);
  if (!text) return undefined;
  return { label: type === "Installation" ? "Installation Address" : "Removal Address", text };
};

function mapBatchInvoiceLicenseType(raw?: string): string {
  const s = String(raw || "").trim().toUpperCase().replace(/[\s-]+/g, "_");
  if (s === "SIGNAGE_TEAM_MEMBER" || s === "SIGNAGE_TEAMMEMBER") return "SIGNAGE_TEAM_MEMBER";
  if (s === "SIGNAGE_FIRM" || s === "SIGNAGE") return "SIGNAGE_FIRM";
  if (s === "AGENT" || s === "BROKERAGE_AGENT") return "AGENT";
  if (
    s === "BROKERAGE_TEAM_MEMBER" ||
    s === "TEAM_MEMBER" ||
    s === "TEAMMEMBER" ||
    s === "BROKERAGE_TEAMMEMBER"
  ) {
    return "BROKERAGE_TEAM_MEMBER";
  }
  if (s === "BROKERAGE_FIRM" || s === "BROKERAGE" || s === "FIRM" || s === "COMPANY") {
    return "BROKERAGE_FIRM";
  }
  if (s.includes("AGENT")) return "AGENT";
  if (s.includes("SIGNAGE")) return "SIGNAGE_FIRM";
  if (s.includes("TEAM")) return "BROKERAGE_TEAM_MEMBER";
  return "BROKERAGE_FIRM";
}

/* =========================
   Small blocks
========================= */
const CompanyBlock = ({ c }: { c: Company }) => (
  <div className="small">
    <div className="fw-semibold">{c.name}</div>
    <div>{c.address1}</div>
    {c.address2 && <div>{c.address2}</div>}
    {c.phone && <div>Phone: {c.phone}</div>}
    {c.email && <div>Email: {c.email}</div>}
  </div>
);

const PartyBlock = ({
  title,
  p,
  showAddress = false,
  showPhone = false,
}: {
  title: string;
  p: Party;
  showAddress?: boolean;
  showPhone?: boolean;
}) => {
  const nameLines = (p.name || "")
    .split(",")
    .map((line) => line.trim())
    .filter(Boolean);
  const lastIndex = nameLines.length - 1;
  const addressLines = !showAddress
    ? []
    : p.addressLabel
    ? (p.addressLabel || "")
        .split(",")
        .map((line) => line.trim())
        .filter(Boolean)
    : [p.address1, p.address2].map((line) => (line || "").trim()).filter(Boolean);
  const addressLastIndex = addressLines.length - 1;

  return (
    <div>
      <div className="fw-semibold text-uppercase small text-muted mb-2">{title}</div>
      {nameLines.length > 0 ? (
        nameLines.map((line, idx) => (
          <div key={idx} className="fw-semibold">
            {line}
            {nameLines.length > 1 ? (idx < lastIndex ? "," : ".") : ""}
          </div>
        ))
      ) : (
        <div className="fw-semibold">{p.name}</div>
      )}
      {addressLines.map((line, idx) => (
        <div key={`addr-${idx}`} className="fw-semibold">
          {line}
          {addressLines.length > 0 ? (idx < addressLastIndex ? "," : ".") : ""}
        </div>
      ))}
      {/* {showPhone && p.phone && <div>Phone: {p.phone}</div>} */}
    </div>
  );
};

/* =========================
   Orders Table
========================= */
const OrdersTable = ({ rows }: { rows: OrderRow[] }) => {
  const computed = rows.map((r) => {
    const itemsTotal = (r.items ?? []).reduce((s, i) => s + i.total, 0);
    const amount = r.amount ?? itemsTotal;
    const paid = r.paid ?? 0;
    return { ...r, _amount: amount, _paid: paid, _balance: amount - paid };
  });

  return (
    <div className="table-responsive">
      <table className="table table-bordered invoice-table">
        <colgroup>
          <col style={{ width: "150px" }} />
          <col style={{ width: "95px" }} />
          <col style={{ width: "150px" }} />
          <col style={{ width: "150px" }} />
          <col style={{ width: "100px" }} />
          <col style={{ width: "100px" }} />
          {/* Amount / Paid / Balance Due columns hidden for now */}
          {/* <col style={{ width: "100px" }} />
          <col style={{ width: "100px" }} />
          <col style={{ width: "150px" }} /> */}
        </colgroup>

        <thead className="table-light align-middle">
          <tr>
            <th>Order ID</th>
            <th>Type</th>
            <th>Office</th>
            <th>Agent / Team Member</th>
            <th>Order Date</th>
            <th>Due Date</th>
            {/* <th className="text-end">Amount</th>
            <th className="text-end">Paid</th>
            <th className="text-end">Balance Due</th> */}
          </tr>
        </thead>

        <tbody>
          {computed.map((r, groupIdx) => {
            const items = [...(r.items ?? []), ...(r.costLines ?? [])];
            const alt = groupIdx % 2 === 1 ? "alt" : ""; // <-- alternate band

            return (
              <React.Fragment key={r.orderId + groupIdx}>
                {/* Main order row */}
                <tr className={`align-middle order-row ${alt}`}>
                  <td className="fw-semibold">{r.orderId}</td>
                  <td>{r.type}</td>
                  <td>{r.office}</td>
                  <td>{r.agent}</td>
                  <td>{r.orderDate}</td>
                  <td>{r.dueDate}</td>
                  {/* <td className="text-end fw-semibold">{currency(r._amount)}</td>
                  <td className="text-end">{currency(r._paid)}</td>
                  <td className="text-end fw-semibold">{currency(r._balance)}</td> */}
                </tr>

                {r.address && (
                  <tr className={`item-row ${alt}`}>
                    <td className="fw-semibold">{r.address.label}</td>
                    <td colSpan={5}>{r.address.text}</td>
                  </tr>
                )}

                {/* Items header (keeps its blue strip, not part of banding) */}
                {items.length > 0 && (
                  <tr className="items-header-row">
                    <td colSpan={3} className="items-h">Item Description</td>
                    <td className="items-h">Qty</td>
                    <td className="items-h text-end">Unit Price</td>
                    <td className="items-h text-end">Total</td>
                  </tr>
                )}

                {!r.hasDetail && (
                  <tr className={`item-row ${alt}`}>
                    <td colSpan={6} className="text-muted fst-italic">
                      No line-item details available
                    </td>
                  </tr>
                )}

                {/* Item lines inherit the group's band color */}
                {items.map((it, i) => (
                  <tr key={i} className={`item-row ${alt}`}>
                    <td colSpan={3} title={it.description}>{it.description}</td>
                    <td>{it.isCost ? "—" : it.qty}</td>
                    <td className="text-end">{it.isCost ? "—" : currency(it.unitPrice)}</td>
                    <td className="text-end">
                      {it.isCost && it.total === 0 ? "—" : currency(it.total)}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/* =========================
   Summary
========================= */
const SummaryCard = ({ rows, extra }: { rows: OrderRow[]; extra?: Summary }) => {
  const calculatedItemsTotal = useMemo(
    () => sum(rows.map((r) => sum((r.items ?? []).map((i) => i.total)))),
    [rows]
  );
  const subTotal = extra?.itemsTotal ?? calculatedItemsTotal;
  const discount = extra?.discount ?? 0;
  const installationCost = extra?.installationCost ?? 0;
  const removalCost = extra?.removalCost ?? 0;
  const spec = extra?.specialFee ?? 0;
  const ship = extra?.shipping ?? 0;
  const tax = extra?.tax ?? 0;
  const grand =
    extra?.grandTotal ??
    (subTotal - discount + installationCost + removalCost + spec + ship + tax);
  const paid = extra?.paid ?? 0;
  const outstanding = extra?.outstandingBalance ?? (grand - paid);

  return (
    <div className="summary-card">
      <div className="summary-title">Summary</div>
      <div className="summary-row">
        <span className="summary-label">Sub Total:</span>
        <span className="summary-value">{currency(subTotal)}</span>
      </div>
      <div className="summary-row">
        <span className="summary-label">Discount:</span>
        <span className="summary-value">{currency(-discount)}</span>
      </div>
      <div className="summary-row">
        <span className="summary-label">Installation Cost:</span>
        <span className="summary-value">{currency(installationCost)}</span>
      </div>
      <div className="summary-row">
        <span className="summary-label">Removal Cost:</span>
        <span className="summary-value">{currency(removalCost)}</span>
      </div>
      <div className="summary-row">
        <span className="summary-label">Special Fee:</span>
        <span className="summary-value">{currency(spec)}</span>
      </div>
      <div className="summary-row">
        <span className="summary-label">Shipping:</span>
        <span className="summary-value">{currency(ship)}</span>
      </div>
      <div className="summary-row">
        <span className="summary-label">Tax:</span>
        <span className="summary-value">{currency(tax)}</span>
      </div>
      <div className="summary-divider" />
      <div className="summary-row summary-grand summary-grand-total">
        <span className="summary-grand-label">Grand Total:</span>
        <span className="summary-grand-value">{currency(grand)}</span>
      </div>
      <div className={`summary-row summary-paid${paid > 0 ? " is-positive" : ""}`}>
        <span className="summary-label">Paid:</span>
        <span className="summary-value">{currency(paid)}</span>
      </div>
      <div className="summary-divider" />
      <div
        className={`summary-row summary-grand summary-outstanding${
          outstanding <= 0 ? " is-settled" : " is-due"
        }`}
      >
        <span className="summary-grand-label">Outstanding Balance:</span>
        <span className="summary-grand-value">{currency(outstanding)}</span>
      </div>
    </div>
  );
};

/* =========================
   Page
========================= */
const BatchPaymentReportPage: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  /** Email "click to pay" uses /public/batchPaymentReport/:id — restricted UI */
  const isPublic = location.pathname.includes("/public/");
  const isSignageLogin =
    location.pathname.includes("/signage") && !location.pathname.includes("/signageteammember");
  const isSignageTeammemberLogin = location.pathname.includes("/signageteammember");
  const allowPartialPayment = isSignageLogin || isSignageTeammemberLogin;
  const [reportData, setReportData] = useState<any>(null);
  const [reportDetails, setReportDetails] = useState<any>(null);
  const [billFromAddress, setBillFromAddress] = useState<any>(null);
  const [billToAddress, setBillToAddress] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [showSendInvoiceModal, setShowSendInvoiceModal] = useState(false);
  const [sendInvoiceLoading, setSendInvoiceLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [invoiceActionLoading, setInvoiceActionLoading] = useState<"finalize" | "cancel" | null>(null);
  const token = isPublic ? "" : getAuthCookie("sessionToken") || "";
  const userEmail = String(getAuthCookie("email") || "").trim();

  useEffect(() => {
    const fetchReportData = async () => {
      if (!reportId) {
        setError("No report ID provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        if (!isPublic && !token) {
          throw new Error("No authentication token found");
        }

        // First API call: Get report by ID
        // const reportResponse = await fetch(
        //   `${process.env.REACT_APP_API_URL}/batch-payments/reports/${reportId}`,
        //   {
        //     method: "GET",
        //     headers: {
        //       Authorization: `Bearer ${token}`,
        //       "Content-Type": "application/json",
        //     },
        //   }
        // );

        // if (!reportResponse.ok) {
        //   throw new Error(`Failed to fetch report: ${reportResponse.statusText}`);
        // }

        // const baseReportData = await reportResponse.json();
        // setReportData(baseReportData);

        // Second API call: Get report details with filters and bill_meta
        // const billMeta = {
        //   bill_from_name: baseReportData.bill_from?.name || "",
        //   bill_to_name: baseReportData.bill_to?.name || "",
        //   bill_to_members: baseReportData.bill_to_members || []
        // };

        // const originalFilters = baseReportData.filters || {};
        // const normalizeEntry = (entry: any) => {
        //   if (!entry) return null;
        //   if (typeof entry === "string") return { id: entry, name: "" };
        //   return {
        //     id: entry.id ?? entry._id ?? "",
        //     name:
        //       entry.name ??
        //       entry.label ??
        //       entry.text ??
        //       [entry.firstName, entry.lastName].filter(Boolean).join(" ") ??
        //       "",
        //   };
        // };
        // const normalizeCollection = (collection: any) =>
        //   Array.isArray(collection)
        //     ? collection
        //         .map(normalizeEntry)
        //         .filter((item) => item && item.id)
        //     : [];

        // const detailFilters: any = {
        //   payer_scope:
        //     originalFilters.payer_scope ||
        //     (originalFilters.bill_to_category === "brokerage"
        //       ? "brokerage"
        //       : originalFilters.bill_to_category ?? null),
        //   company_id:
        //     originalFilters.company_id ||
        //     baseReportData.bill_to?.id ||
        //     baseReportData.bill_from?.id ||
        //     null,
        //   group_id: originalFilters.group_id ?? null,
        //   office_ids: normalizeCollection(originalFilters.office_ids),
        //   agent_ids: normalizeCollection(originalFilters.agent_ids),
        //   team_member_ids: normalizeCollection(originalFilters.team_member_ids),
        //   bill_to_category: originalFilters.bill_to_category ?? "brokerage",
        //   order_types: originalFilters.order_types ?? [],
        //   status: originalFilters.status ?? [],
        //   search: originalFilters.search ?? "",
        //   date_from: originalFilters.date_from ?? null,
        //   date_to: originalFilters.date_to ?? null,
        // };

        // const billMembers = billMeta.bill_to_members || [];
        // const fillFromMembers = (
        //   type: string,
        //   key: "office_ids" | "agent_ids" | "team_member_ids"
        // ) => {
        //   if (detailFilters[key] && detailFilters[key].length) return;
        //   const matches = billMembers
        //     .filter((member: any) => member?.type === type)
        //     .map((member: any) => normalizeEntry(member))
        //     .filter(Boolean);
        //   if (matches.length) detailFilters[key] = matches;
        // };

        // fillFromMembers("office", "office_ids");
        // fillFromMembers("agent", "agent_ids");
        // fillFromMembers("team_member", "team_member_ids");

        // if (!detailFilters.company_id && baseReportData.bill_to_members) {
        //   const companyMember = billMembers.find(
        //     (member: any) => member?.type === "company" || member?.type === "brokerage"
        //   );
        //   if (companyMember?.id) {
        //     detailFilters.company_id = companyMember.id;
        //   }
        // }

        // if (detailFilters.company_id === null) {
        //   delete detailFilters.company_id;
        // }
 
        const fromPublic = window.location.pathname.includes('/public/');

        const detailsEndpoint = fromPublic
          ? `/public/batch-payments/reports/${reportId}/detail`
          : `/batch-payments/reports/${reportId}/detail`;

        const detailsHeaders: Record<string, string> = {
          "Content-Type": "application/json",
        };
        if (!fromPublic && token) {
          detailsHeaders.Authorization = `Bearer ${token}`;
        }

        const detailsResponse = await fetch(
          `${process.env.REACT_APP_API_URL}${detailsEndpoint}`,
          {
            method: "POST",
            headers: detailsHeaders,
            body: JSON.stringify({
              // page: 1,
              // limit: 10,
              // sort_field: "order_date",
              // sort_dir: -1,
              // filters: detailFilters,
              // bill_meta: billMeta
            })
          }
        );

        if (!detailsResponse.ok) {
          throw new Error(`Failed to fetch report details: ${detailsResponse.statusText}`);
        }

        const detailsData = await detailsResponse.json();
        setReportDetails(detailsData);

        // Third API call: Get addresses for bill_from and bill_to
        // const billFromId = detailsData.bill_from?.id ?? baseReportData.bill_from?.id;
        // const billToId = detailsData.bill_to?.id ?? baseReportData.bill_to?.id;

        // Fetch addresses in parallel
        // const addressPromises = [];
        
        // if (billFromId) {
        //   addressPromises.push(
        //     fetch(
        //       `${process.env.REACT_APP_API_URL}/user-addresses/${billFromId}`,
        //       {
        //         method: "GET",
        //         headers: {
        //           Authorization: `Bearer ${token}`,
        //           "Content-Type": "application/json",
        //         },
        //       }
        //     ).then(response => {
        //       if (!response.ok) {
        //         console.warn(`Failed to fetch bill from address: ${response.statusText}`);
        //         return null;
        //       }
        //       return response.json();
        //     }).catch(err => {
        //       console.warn("Error fetching bill from address:", err);
        //       return null;
        //     })
        //   );
        // }

        // if (billToId) {
        //   addressPromises.push(
        //     fetch(
        //       `${process.env.REACT_APP_API_URL}/user-addresses/${billToId}`,
        //       {
        //         method: "GET",
        //         headers: {
        //           Authorization: `Bearer ${token}`,
        //           "Content-Type": "application/json",
        //         },
        //       }
        //     ).then(response => {
        //       if (!response.ok) {
        //         console.warn(`Failed to fetch bill to address: ${response.statusText}`);
        //         return null;
        //       }
        //       return response.json();
        //     }).catch(err => {
        //       console.warn("Error fetching bill to address:", err);
        //       return null;
        //     })
        //   );
        // }

        // Wait for all address calls to complete
        // const addressResults = await Promise.all(addressPromises);
        
        // if (addressResults[0]) {
        //   setBillFromAddress(addressResults[0][0]); // Take first address
        // }
        
        // if (addressResults[1]) {
        //   setBillToAddress(addressResults[1][0]); // Take first address
        // }

      } catch (err) {
        console.error("Error fetching report data:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch report data");
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [reportId, token, reloadKey, isPublic]);

  const handleSendInvoiceEmail = useCallback(
    async (selectedEmails: string[] = [], selectedPeople: SendOrderEmailRecipient[] = []) => {
      if (sendInvoiceLoading) return;
      if (!token) {
        showError("Session expired. Please sign in again.");
        return;
      }

      const recipients = Array.from(
        new Set(
          (selectedEmails || [])
            .map((e) => String(e || "").trim().split("||")[0].trim())
            .filter((e) => e && e.includes("@"))
        )
      );

      if (recipients.length === 0) {
        showError("Select at least one recipient email to send the invoice.");
        return;
      }

      const licenseByEmail = new Map(
        (selectedPeople || []).map((person) => [
          String(person.email || "").trim().toLowerCase(),
          mapBatchInvoiceLicenseType(person.licenseType),
        ])
      );
      const licenseTypes = recipients.map(
        (email) => licenseByEmail.get(email.toLowerCase()) || "BROKERAGE_FIRM"
      );

      const fromEmail =
        process.env.REACT_APP_FROM_EMAIL2?.trim() ||
        String(reportDetails?.bill_from_contact?.email || "").trim().split("||")[0].trim() ||
        userEmail ||
        "noreply@realesy.com";
      const reportMongoId = String(reportId || "").trim();
      // const fileLabel =
      //   String(reportDetails?.report_id || reportMongoId || "batch-invoice").replace(/[^\w.-]+/g, "_");

      setSendInvoiceLoading(true);
      try {
        const isDraftInvoice =
          String(reportDetails?.invoice_status ?? "").trim().toLowerCase() === "draft";
        if (isDraftInvoice) {
          const finalizeResponse = await fetch(
            `${process.env.REACT_APP_API_URL}/batch-payments/reports/${encodeURIComponent(reportMongoId)}/finalize`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          if (!finalizeResponse.ok) {
            let message = `Failed to finalize invoice (${finalizeResponse.status})`;
            try {
              const errBody = await finalizeResponse.json();
              message = String(errBody?.message || errBody?.error || message);
            } catch {
              /* ignore */
            }
            throw new Error(message);
          }
        }

        // const pdfAttachment = await buildBatchPaymentReportInvoicePdfAttachment({
        //   reportId: reportMongoId,
        //   token,
        //   fileLabel,
        //   pdfFetch: {
        //     maxAttempts: 8,
        //     initialDelayMs: 400,
        //     retryDelayMs: 600,
        //     perFetchTimeoutMs: 20_000,
        //   },
        // });
        // if (!pdfAttachment) {
        //   throw new Error("Could not fetch invoice PDF from the server.");
        // }

        const baseParams = buildBatchInvoiceEmailParams(reportDetails, reportMongoId);
        // baseParams.viewPayUrl = await buildBatchInvoicePublicViewPayUrl(
        //   reportDetails,
        //   token,
        //   reportMongoId,
        //   String(getAuthCookie("organizationId") || "").trim()
        // );

        await sendBatchInvoiceTriggerEmails(
          baseParams,
          recipients,
          licenseTypes,
          fromEmail,
          token
          // pdfAttachment
        );
        showSuccess("Invoice email sent.");
        if (isDraftInvoice) setReloadKey((k) => k + 1);
      } catch (e) {
        showError(e instanceof Error ? e.message : "Failed to send invoice email.");
        throw e;
      } finally {
        setSendInvoiceLoading(false);
      }
    },
    [sendInvoiceLoading, token, reportDetails, reportId, userEmail]
  );

  const handleDownloadInvoicePdf = useCallback(async () => {
    if (downloadingPdf) return;
    const id = String(reportId || "").trim();
    if (!id) {
      showError("No report ID found.");
      return;
    }

    // Mirror detail fetch: public uses unauthenticated public PDF route; private needs Bearer.
    const pdfPath = isPublic
      ? `/public/batch-payments/reports/${encodeURIComponent(id)}/invoice-pdf`
      : `/batch-payments/reports/${encodeURIComponent(id)}/invoice-pdf`;

    const authToken = token || getAuthCookie("sessionToken") || "";
    if (!isPublic && !authToken) {
      showError("Session expired. Please sign in again.");
      return;
    }

    setDownloadingPdf(true);
    try {
      const headers: Record<string, string> = {};
      if (!isPublic && authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}${pdfPath}`, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to download invoice PDF (${response.status})`);
      }

      const blob = await response.blob();
      const cd = response.headers.get("Content-Disposition");
      let filename = `batch-invoice-${id}.pdf`;
      if (cd) {
        const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(cd);
        const plain = /filename="?([^";]+)"?/i.exec(cd);
        if (utf8?.[1]) filename = decodeURIComponent(utf8[1].trim());
        else if (plain?.[1]) filename = plain[1].trim();
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showSuccess("Invoice PDF downloaded.");
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to download invoice PDF.");
    } finally {
      setDownloadingPdf(false);
    }
  }, [downloadingPdf, reportId, token, isPublic]);

  const postInvoiceStatusAction = useCallback(
    async (action: "finalize" | "cancel") => {
      if (isPublic || invoiceActionLoading) return;
      const id = String(reportId || "").trim();
      if (!id) {
        showError("No report ID found.");
        return;
      }
      if (!token) {
        showError("Session expired. Please sign in again.");
        return;
      }

      const path =
        action === "finalize"
          ? `/batch-payments/reports/${encodeURIComponent(id)}/finalize`
          : `/batch-payments/reports/${encodeURIComponent(id)}/cancel`;

      setInvoiceActionLoading(action);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        if (!response.ok) {
          let message = `Failed to ${action} report (${response.status})`;
          try {
            const errBody = await response.json();
            message = String(errBody?.message || errBody?.error || message);
          } catch {
            /* ignore */
          }
          throw new Error(message);
        }
        showSuccess(action === "finalize" ? "Invoice finalized." : "Invoice cancelled.");
        setReloadKey((k) => k + 1);
      } catch (e) {
        showError(e instanceof Error ? e.message : `Failed to ${action} report.`);
      } finally {
        setInvoiceActionLoading(null);
      }
    },
    [isPublic, invoiceActionLoading, reportId, token]
  );

  const paymentLogEvents = useMemo(
    () =>
      buildBatchPaymentLogEventsFromReport(
        Array.isArray(reportDetails?.payment_refs) ? reportDetails.payment_refs : [],
        {
          reportPaymentStatus: String(reportDetails?.payment_status ?? ""),
          reportRemainingBalance: Number(reportDetails?.report_remaining_balance ?? 0),
        }
      ),
    [reportDetails]
  );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <span className="ms-2">Loading report...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container my-3">
        <div className="alert alert-danger">
          <h4>Error Loading Report</h4>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => window.history.back()}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!reportDetails) {
    return (
      <div className="container my-3">
        <div className="alert alert-warning">
          <h4>Report Not Found</h4>
          <p>No report data found. Please generate a new report.</p>
          <button className="btn btn-primary" onClick={() => window.history.back()}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Prefer detail response for main report data (fallback to base report)
  const resolvedBillFrom = (reportDetails?.bill_from ?? reportData?.bill_from) || {};
  const resolvedBillTo = (reportDetails?.bill_to ?? reportData?.bill_to) || {};
  const resolvedBillFromContact =
    reportDetails?.bill_from_contact ?? reportData?.bill_from_contact ?? {};
  const resolvedReportDate = reportDetails?.report_date ?? reportData?.report_date;
  const resolvedReportPeriod = reportDetails?.report_period ?? reportData?.report_period;
  const resolvedReportId = reportDetails?.report_id ?? reportData?.report_id;
  // Order, item and totals content all come from one version (snapshot or live).
  // Paid / outstanding come from live report fields outside totals.
  const reportVersion = pickReportVersion(reportDetails);
  const orderSource = reportVersion.rows.length > 0 ? reportVersion.rows : reportVersion.details;
  const totals = reportVersion.totals;

  const detailLookup = new Map<string, any>(
    reportVersion.details.map((detail: any) => [String(detail?.order_id ?? ""), detail])
  );

  const orders: OrderRow[] = orderSource.map((row: any) => {
    const detail = detailLookup.get(String(row?.order_id ?? ""));
    const orderType = resolveOrderType(firstText(detail?.order_type, row?.order_type));
    const amount = Number(row?.original ?? detail?.amount ?? detail?.original ?? 0);
    const paid = Number(row?.paid ?? detail?.paid ?? 0);
    const items: LineItem[] = Array.isArray(detail?.items) ? detail.items.map(toLineItem) : [];

    return {
      orderId: firstText(row?.order_number, detail?.order_number, row?.order_id),
      type: orderType,
      office: firstText(row?.office?.name, detail?.office?.name) || "—",
      agent:
        firstText(row?.agent?.name, row?.team_member?.name, detail?.agent?.name) || "—",
      orderDate: formatReportDate(row?.order_date, detail?.order_date),
      dueDate: formatReportDate(row?.due_date, detail?.due_date),
      amount,
      paid,
      balance: Number(row?.balance ?? detail?.balance ?? amount - paid),
      hasDetail: Boolean(detail),
      address: toOrderAddress(detail, row, orderType),
      items,
      costLines: detail ? toCostLines(detail, orderType, items) : [],
    };
  });

  // Set up the data for the report
  const billFromLocation = resolvedBillFromContact?.location;

  const formattedBillFromLabel = billFromLocation
    ? [billFromLocation.addressLine1, billFromLocation.city, billFromLocation.stateCode, billFromLocation.zip]
        .filter(Boolean)
        .join(", ")
    : undefined;

  const company = {
    name: resolvedBillFrom?.name || "",
    address1: billFromLocation?.addressLine1 || billFromAddress?.address_details?.addressLine1 || "",
    address2:
      billFromLocation?.city || billFromLocation?.stateCode || billFromLocation?.zip
        ? `${billFromLocation?.city || billFromAddress?.address_details?.city}, ${
            billFromLocation?.stateCode || billFromAddress?.address_details?.state} 
            ${billFromLocation?.zip || billFromAddress?.address_details?.zip}`
        : `${billFromAddress?.address_details?.city}, ${
            billFromAddress?.address_details?.state
          } ${billFromAddress?.address_details?.zip}`,
    phone:
      resolvedBillFromContact?.phone ||
      billFromLocation?.phone ||
      billFromAddress?.address_details?.phone,
    email: resolvedBillFromContact?.email || "",
  };

  const billFrom = {
    name: resolvedBillFrom?.name || "",
    addressLabel: formattedBillFromLabel,
    address1: formattedBillFromLabel ? undefined : billFromAddress?.address_details?.addressLine1 || "",
    address2: formattedBillFromLabel
      ? undefined
      : `${billFromAddress?.address_details?.city || ""}, ${billFromAddress?.address_details?.state || ""} ${
          billFromAddress?.address_details?.zip || ""
        }`,
    phone:
      resolvedBillFromContact?.phone ||
      billFromLocation?.phone ||
      billFromAddress?.address_details?.phone ||
      ""
  };

  const billTo = {
    name: resolvedBillTo?.name,
    address1: billToAddress?.address_details?.addressLine1 || "",
    address2: `${billToAddress?.address_details?.city || ""}, ${billToAddress?.address_details?.state || ""} ${billToAddress?.address_details?.zip || ""}`,
    phone: billToAddress?.address_details?.phone || ""
  };

  const reportDate = resolvedReportDate ? new Date(resolvedReportDate).toLocaleDateString() : "";
  const reportPeriod = resolvedReportPeriod;
  const reportIdValue = resolvedReportId;
  const invoiceStatus = String(reportDetails?.invoice_status ?? "").trim();
  const headerPaymentStatus = String(reportDetails?.payment_status ?? "").trim();
  const invoiceStatusLower = invoiceStatus.toLowerCase();
  const showSignageInvoiceActions = !isPublic && (isSignageLogin || isSignageTeammemberLogin);
  const showFinalizeAction = showSignageInvoiceActions && invoiceStatusLower === "draft";
  const showCancelAction = showSignageInvoiceActions && invoiceStatusLower === "finalized";
  
  // Every Summary line comes from the Details totals object.
  // Paid / outstanding always read the live `totals`, even when line amounts are frozen.
  const liveTotals = reportDetails?.totals ?? {};
  const summary = {
    itemsTotal: Number(totals?.sub_total ?? totals?.items_total ?? 0),
    discount: Number(totals?.discount ?? 0),
    installationCost: Number(totals?.installation_cost ?? 0),
    removalCost: Number(totals?.removal_cost ?? 0),
    specialFee: Number(totals?.special_fee ?? 0),
    shipping: Number(totals?.shipping ?? 0),
    tax: Number(totals?.tax ?? 0),
    grandTotal: Number(totals?.grand_total ?? 0),
    paid: Number(liveTotals?.paid ?? totals?.paid ?? 0),
    outstandingBalance: Number(liveTotals?.outstanding_balance ?? totals?.outstanding_balance ?? 0),
  };

  // Payment props from report (same PaymentSection as SignageBatchPayment)
  const filters = reportDetails?.filters ?? {};
  const idList = (list: any): string[] =>
    (Array.isArray(list) ? list : [])
      .map((x) => (typeof x === "string" ? x : String(x?.id ?? x?._id ?? "")).trim())
      .filter(Boolean);

  // Orders to pay come from the selected version; balances stay live (from `rows`).
  const liveRowLookup = new Map<string, any>(
    (hasEntries(reportDetails?.rows) ? reportDetails.rows : []).map((row: any) => [
      String(row?.order_id ?? ""),
      row,
    ])
  );

  const paymentOrders = orderSource
    .map((row: any) => {
      const orderId = String(row?.order_id ?? "").trim();
      const detail = detailLookup.get(orderId);
      const liveRow = liveRowLookup.get(orderId);
      const amount = Number(liveRow?.balance ?? row?.balance ?? detail?.balance ?? 0);
      if (!orderId || amount <= 0) return null;
      return {
        order_id: orderId,
        order_type: (firstText(detail?.order_type, row?.order_type) || "sales").toLowerCase(),
        order_number: firstText(row?.order_number, detail?.order_number),
        bill_to: String(resolvedBillTo?.name ?? ""),
        amount,
      };
    })
    .filter(Boolean) as Array<{
      order_id: string;
      order_type: string;
      order_number: string;
      bill_to: string;
      amount: number;
    }>;

  const selectedBalance = paymentOrders.reduce((s, o) => s + Number(o.amount || 0), 0);
  const outstanding = Number(summary.outstandingBalance || selectedBalance || 0);
  const showPayment = outstanding > 0 && paymentOrders.length > 0;

  // Only for Paid reports: show status + paid amount instead of Payment + Summary
  const reportPaymentStatus = String(reportDetails?.payment_status ?? "").trim();
  const hasPaidAmount =
    reportDetails?.paid_amount !== null && reportDetails?.paid_amount !== undefined;
  const paymentStatusLower = reportPaymentStatus.toLowerCase();
  const isPaymentSettled =
    paymentStatusLower === "paid" || paymentStatusLower === "overpaid" || outstanding <= 0;
  const showPaidStatusSummary =
    paymentStatusLower === "paid" && hasPaidAmount;
  const hidePublicPaymentCard = isPublic && isPaymentSettled;
  const showPaymentForm =
    showPayment &&
    !showPaidStatusSummary &&
    !hidePublicPaymentCard &&
    invoiceStatusLower !== "cancelled";
  const reportPaidAmount = Number(reportDetails?.paid_amount ?? 0);
  const paymentRefs = Array.isArray(reportDetails?.payment_refs) ? reportDetails.payment_refs : [];
  const latestPaymentRef = paymentRefs.length > 0 ? paymentRefs[paymentRefs.length - 1] : null;
  const paidByName = String(
    reportDetails?.paid_by?.name ??
      latestPaymentRef?.paid_by?.name ??
      ""
  ).trim();

  const companyId =
    typeof filters.company_id === "object"
      ? String(filters.company_id?.id ?? filters.company_id?._id ?? "")
      : String(filters.company_id ?? "");
  const companyName =
    (typeof filters.company_id === "object" ? filters.company_id?.name : "") ||
    resolvedBillTo?.name ||
    companyId;

  const readId = (value: any) => {
    const id = String(
      value && typeof value === "object"
        ? value.organization_id ?? value.organizationId ?? value.seller_org_id ?? value.id ?? value._id ?? ""
        : value ?? ""
    ).trim();
    if (!id || id === "000000000000000000000000" || id === "undefined" || id === "null") return "";
    return id;
  };
  const signageOrgFromRows = (list: any) => {
    if (!Array.isArray(list)) return "";
    for (const row of list) {
      const id =
        readId(row?.signage) ||
        readId(row?.seller_org_id) ||
        readId(row?.signage_id);
      if (id) return id;
    }
    return "";
  };
  const sellerFilterIds = reportDetails?.filters?.seller_organization_ids;
  const billFromOrgId =
    readId(resolvedBillFrom) ||
    signageOrgFromRows(reportDetails?.rows) ||
    signageOrgFromRows(reportDetails?.snapshot_rows) ||
    signageOrgFromRows(reportVersion.rows) ||
    (Array.isArray(sellerFilterIds) ? readId(sellerFilterIds[0]) : "");
  const selectedVendor = billFromOrgId
    ? {
        supplier_id: billFromOrgId,
        organization_id: billFromOrgId,
        name: String(resolvedBillFrom?.name ?? ""),
        email: String(resolvedBillFromContact?.email ?? ""),
        linked_at: "",
      }
    : null;


  return (
    <>
      <div className="container my-3 report-container">
        <style>{`
          /* ===== Screen styles ===== */
          .report-container { font-size: .95rem; }

          .invoice-table, .invoice-table th, .invoice-table td { border-color: #e5e7eb !important; }
          .invoice-table thead th { background-color: #f8f9fa !important; }
          .va-top { vertical-align: top !important; }

          .items-header-row .items-h { background: #e9f0ff !important; color: #193cb8; font-weight: 600; }

          .order-row td, .item-row td { background: #ffffff; }
          .order-row.alt td, .item-row.alt td { background: #f6f7f9; }

          .summary-card {
            width: 300px; border: 1px solid #e5e7eb; border-radius: 15px;
            padding: 20px 22px; background: #fff; font-size: 0.95rem;
          }
          .summary-title { font-weight: 600; margin-bottom: 16px; }
          .summary-row { display: flex; justify-content: space-between; margin-bottom: 12px; line-height: 1.4; }
          .summary-label { color: #6b7280; }
          .summary-value { font-weight: 600; }
          .summary-divider { border-top: 1px solid #e5e7eb; margin: 18px 0; }
          .summary-grand { font-size: 1.1rem; }
          .summary-grand-label { font-weight: 600; }
          .summary-grand-value { font-weight: 700; }

          /* Financial emphasis: total = brand blue, paid = green, due = red / settled = green */
          .summary-grand-total .summary-grand-label,
          .summary-grand-total .summary-grand-value {
            color: #101828;
          }
          .summary-paid.is-positive .summary-label,
          .summary-paid.is-positive .summary-value {
            color: #059669;
          }
          .summary-paid.is-positive .summary-value {
            font-weight: 700;
          }
          .summary-outstanding.is-due .summary-grand-label,
          .summary-outstanding.is-due .summary-grand-value {
            color: #dc2626;
          }
          .summary-outstanding.is-settled .summary-grand-label,
          .summary-outstanding.is-settled .summary-grand-value {
            color: #059669;
          }

          .bill-row { display: flex; gap: 48px; }
          .bill-col { flex: 1 1 0; }

          /* Keep Actions button distinct from page background (incl. open/focus) */
          .actions-dropdown > .btn.back-btn,
          .actions-dropdown > .btn.back-btn:hover,
          .actions-dropdown > .btn.back-btn:focus,
          .actions-dropdown > .btn.back-btn:active,
          .actions-dropdown.show > .btn.back-btn,
          .actions-dropdown > .btn.back-btn.show {
            background: #ffffff !important;
            border: 1px solid #d0d5dd !important;
            color: #101828 !important;
            box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
          }
          .actions-dropdown > .btn.back-btn:focus,
          .actions-dropdown > .btn.back-btn.show {
            outline: none;
            box-shadow: 0 0 0 2px rgba(21, 93, 252, 0.15), 0 1px 2px rgba(16, 24, 40, 0.08);
          }

          .actions-dropdown .dropdown-item.action-finalize {
            background: #dbeafe;
            color: #1d4ed8;
            font-weight: 600;
          }
          .actions-dropdown .dropdown-item.action-finalize:hover,
          .actions-dropdown .dropdown-item.action-finalize:focus {
            background: #155dfc !important;
            color: #ffffff !important;
          }
          .actions-dropdown .dropdown-item.action-cancel {
            background: #fee2e2;
            color: #b91c1c;
            font-weight: 600;
          }
          .actions-dropdown .dropdown-item.action-cancel:hover,
          .actions-dropdown .dropdown-item.action-cancel:focus {
            background: #dc2626 !important;
            color: #ffffff !important;
          }

          @media (max-width: 576px) {
            .bill-row { flex-direction: column; gap: 24px; }
          }

          /* ===== @page: A4, equal margins, page number bottom-right ===== */
          @page {
            size: A4 portrait;
            margin: 12mm 12mm 20mm 12mm;
            @bottom-right {
              content: counter(page) " of " counter(pages);
              font-size: 11px;
              color: #6b7280;
            }
          }

          /* ===== @media print: show ONLY #batch-report-pdf, full-width ===== */
          @media print {
            body * { visibility: hidden !important; }
            #batch-report-pdf, #batch-report-pdf * { visibility: visible !important; }

            /* Fill the entire @page content area — let margins handle spacing */
            #batch-report-pdf {
              position: static !important;
              display: block !important;
              width: 100% !important;
              max-width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
              box-sizing: border-box !important;
            }

            /* Strip Bootstrap container constraints so no extra gap on any side */
            .report-container,
            .container,
            .container-fluid {
              max-width: 100% !important;
              width: 100% !important;
              padding-left: 0 !important;
              padding-right: 0 !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
            }

            .no-print, .report-actions { display: none !important; }
            .bill-row { display: flex !important; flex-direction: row !important; gap: 32px !important; }
            .bill-col { flex: 1 1 0 !important; }
            .table, .table tr, .table td { break-inside: auto; page-break-inside: auto; }
            thead { display: table-header-group; }
            tfoot { display: table-footer-group; }
            .card, .table, .table-bordered, .table-bordered td, .table-bordered th { box-shadow: none !important; }
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        `}</style>

        {/* Actions — hidden in print via .no-print; public pay link hides back + Send Invoice */}
        <div
          className={`report-actions no-print d-flex align-items-center mb-3 ${
            isPublic ? "justify-content-end" : "justify-content-between"
          }`}
        >
          {!isPublic && (
            <button className="btn back-btn surface-btn fw-bold fs-16" onClick={() => navigate(-1)}>
              <i className="bi bi-arrow-left me-1" />
              Back to Batch Payments
            </button>
          )}
          <div className="dropdown actions-dropdown">
            <button
              className="btn back-btn surface-btn fw-bold fs-16 dropdown-toggle no-caret"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              style={{ minWidth: 140 }}
            >
              <span className="me-1">Actions</span>
              <i className="bi bi-three-dots"></i>
            </button>
            <ul className="dropdown-menu dropdown-menu-end">
              {showSignageInvoiceActions && (
                <li>
                  <button
                    type="button"
                    className="dropdown-item"
                    disabled={sendInvoiceLoading}
                    onClick={() => setShowSendInvoiceModal(true)}
                  >
                    <i className="bi bi-send me-2" />
                    {sendInvoiceLoading ? "Sending…" : "Send Invoice"}
                  </button>
                </li>
              )}
              <li>
                <button
                  type="button"
                  className="dropdown-item"
                  disabled={downloadingPdf}
                  onClick={handleDownloadInvoicePdf}
                >
                  <i className="bi bi-printer me-2" />
                  {downloadingPdf ? "Downloading…" : "Print & Download PDF"}
                </button>
              </li>
              {showFinalizeAction && (
                <li>
                  <button
                    type="button"
                    className="dropdown-item action-finalize"
                    disabled={!!invoiceActionLoading}
                    onClick={() => postInvoiceStatusAction("finalize")}
                  >
                    <i className="bi bi-check2-circle me-2" />
                    {invoiceActionLoading === "finalize" ? "Finalizing…" : "Finalized"}
                  </button>
                </li>
              )}
              {showCancelAction && (
                <li>
                  <button
                    type="button"
                    className="dropdown-item action-cancel"
                    disabled={!!invoiceActionLoading}
                    onClick={() => postInvoiceStatusAction("cancel")}
                  >
                    <i className="bi bi-x-circle me-2" />
                    {invoiceActionLoading === "cancel" ? "Cancelling…" : "Cancelled"}
                  </button>
                </li>
              )}
            </ul>
          </div>
        </div>

        {!isPublic && (
          <SendOrderEmailRecipientsModal
            show={showSendInvoiceModal}
            title="Send Invoice"
            token={token}
            selectedRecipientsOnly
            confirmLabel="OK"
            confirming={sendInvoiceLoading}
            onCancel={() => setShowSendInvoiceModal(false)}
            onConfirm={async (additionalEmails, recipients) => {
              try {
                await handleSendInvoiceEmail(additionalEmails, recipients);
                setShowSendInvoiceModal(false);
              } catch {
                /* keep modal open on failure so user can retry */
              }
            }}
          />
        )}

        {/* Printable report content — isolated via #batch-report-pdf */}
        <div id="batch-report-pdf">
          {/* Top header */}
          <div className="d-flex justify-content-between align-items-start mb-4">
            <CompanyBlock c={company} />
            <div className="text-end small">
              <div className="text-muted">Report Date:</div>
              <div className="fw-semibold">{reportDate}</div>
              <div className="mt-2 text-muted">Report Period:</div>
              <div className="fw-semibold">{reportPeriod}</div>
              <div className="mt-2 text-muted">Batch Invoice ID:</div>
              <div className="fw-semibold">{reportIdValue}</div>
              {invoiceStatus ? (
                <>
                  <div className="mt-2 text-muted">Invoice Status:</div>
                  <div className="fw-semibold">{invoiceStatus}</div>
                </>
              ) : null}
              {headerPaymentStatus ? (
                <>
                  <div className="mt-2 text-muted">Payment Status:</div>
                  <div className="d-flex justify-content-end mt-1">
                    <SalesOrderStatusBadge type="payment" value={headerPaymentStatus} />
                  </div>
                </>
              ) : null}
            </div>
          </div>

          <hr className="my-4" />

          {/* Parties */}
          <div className="bill-row mb-4">
            <div className="bill-col">
              <PartyBlock title="Bill From:" p={billFrom} showAddress showPhone />
            </div>
            <div className="bill-col">
              <PartyBlock title="Bill To:" p={billTo} />
            </div>
          </div>

          {/* Orders + items */}
          <div className="mb-2 fw-semibold">Order Details</div>
          <OrdersTable rows={orders} />

          {/* Paid: Payment status + Summary. Unpaid: Payment form (if needed) + Summary. */}
          <div className="d-flex justify-content-between align-items-start gap-4 my-4 flex-wrap">
            {showPaidStatusSummary ? (
              <div className="flex-grow-1" style={{ minWidth: 280 }}>
                <div className="summary-card">
                  <div className="summary-title">Payment</div>
                  <div className="summary-row">
                    <span className="summary-label">Payment Status:</span>
                    <span className="summary-value">{reportPaymentStatus}</span>
                  </div>
                  {paidByName && (
                    <div className="summary-row">
                      <span className="summary-label">Paid By:</span>
                      <span className="summary-value">{paidByName}</span>
                    </div>
                  )}
                  <div className="summary-divider" />
                  <div className="summary-row summary-grand">
                    <span className="summary-grand-label">Paid Amount:</span>
                    <span className="summary-grand-value">{currency(reportPaidAmount)}</span>
                  </div>
                </div>
              </div>
            ) : (
              showPaymentForm && (
                <div className="flex-grow-1 no-print" style={{ minWidth: 280 }}>
                  <PaymentSection
                    selectedCount={paymentOrders.length}
                    selectedBalance={selectedBalance}
                    selectedOrders={paymentOrders}
                    maxAllowed={outstanding}
                    allowPartialPayment={allowPartialPayment}
                    defaultEmailReceipt={true}
                    defaultMethod="card"
                    variant="report"
                    cardOnly={isPublic}
                    selectedVendor={selectedVendor}
                    reportId={reportIdValue || reportId}
                    scope={String(filters.payer_scope || filters.bill_to_category || "company")}
                    company={companyName}
                    teamMembers={idList(filters.team_member_ids)}
                    offices={idList(filters.office_ids)}
                    agents={idList(filters.agent_ids)}
                    bill_from={String(resolvedBillFrom?.name ?? "")}
                    bill_to={String(resolvedBillTo?.name ?? "")}
                    onPaymentSuccess={() => setReloadKey((k) => k + 1)}
                  />
                </div>
              )
            )}
            <div className="ms-auto">
              <SummaryCard rows={orders} extra={summary} />
            </div>
          </div>

          {paymentLogEvents.length > 0 && (
            <div className="my-4 no-print">
              <OrderLog title="Payment Log" events={paymentLogEvents} />
            </div>
          )}

          <div className="text-center text-muted small mt-4">
            This report was generated on {reportDate}
          </div>
        </div>
      </div>
    </>
  );
};

export default memo(BatchPaymentReportPage);

